import { useEffect, useMemo, useState } from 'react'
import { Button, Form, Modal, Row, Col } from 'react-bootstrap'
import {
  CampaignChannel,
  CampaignSchedule,
  ICampaign,
  IMessageTemplate,
} from '../../../types/campaigns'
import { ChannelBadge } from './ChannelBadge'
import { SUPPORTED_CHANNELS } from '../../../constants/campaigns'

export type CampaignFormValues = {
  name: string
  description?: string
  selectedVariantIds: string[]
  scheduleType: CampaignSchedule
  scheduledTime?: string | null
  recurringRule?: string | null
  templateId?: string | null
  metadata?: Record<string, any>
  targetSegment?: Record<string, any>
  enabledChannels: CampaignChannel[]
  strategy: 'BROADCAST' | 'WATERFALL'
}

type Props = {
  show: boolean
  onHide: () => void
  onSubmit: (values: CampaignFormValues) => Promise<void>
  templates: IMessageTemplate[]
  loading?: boolean
  campaign?: ICampaign | null
}



const channelOptions: CampaignChannel[] = SUPPORTED_CHANNELS

const toInputDate = (value?: string | null) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (num: number) => `${num}`.padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`
}

export const CampaignFormModal = ({
  show,
  onHide,
  onSubmit,
  templates,
  loading,
  campaign,
}: Props) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  // Default to immediate for now as requested to simplify
  const [scheduleType, setScheduleType] = useState<CampaignSchedule>('IMMEDIATE')
  const [scheduledTime, setScheduledTime] = useState('')
  const [recurringValue, setRecurringValue] = useState(1)
  const [recurringUnit, setRecurringUnit] = useState<'hours' | 'days' | 'weeks' | 'months'>('days')
  const [stopCondition, setStopCondition] = useState<'never' | 'after_runs' | 'on_date'>('never')
  const [maxOccurrences, setMaxOccurrences] = useState(10)
  const [recurringEndDate, setRecurringEndDate] = useState('')

  const UNIT_TO_MINUTES: Record<string, number> = { hours: 60, days: 1440, weeks: 10080, months: 43200 }
  const recurringIntervalMinutes = recurringValue * UNIT_TO_MINUTES[recurringUnit]

  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([])
  const [templateId, setTemplateId] = useState('')
  // Hidden fields
  const [metadataJson] = useState('{}')
  const [targetSegmentJson] = useState('{}')

  // Derived state for channels & strategy
  const [enabledChannels, setEnabledChannels] = useState<CampaignChannel[]>([])
  const [strategy, setStrategy] = useState<'BROADCAST' | 'WATERFALL'>('BROADCAST')

  useEffect(() => {
    if (!campaign) {
      setName('')
      setDescription('')
      // setScheduleType('IMMEDIATE')
      // setScheduledTime('')
      // setRecurringRule('')
      setSelectedVariantIds([])
      setTemplateId('')
      // setMetadataJson('{}')
      // setTargetSegmentJson('{}')false)
      setEnabledChannels([])
      setStrategy('BROADCAST')
      return
    }
    setName(campaign.name)
    setDescription(campaign.description ?? '')
    setScheduleType(campaign.scheduleType as CampaignSchedule)
    setScheduledTime(toInputDate(campaign.scheduledTime))
    const interval = Number(campaign.metadata?.recurring_interval_minutes || campaign.metadata?.interval_minutes || 1440)
    // Convert stored minutes back to the best unit
    if (interval % 43200 === 0) {
      setRecurringUnit('months'); setRecurringValue(interval / 43200)
    } else if (interval % 10080 === 0) {
      setRecurringUnit('weeks'); setRecurringValue(interval / 10080)
    } else if (interval % 1440 === 0) {
      setRecurringUnit('days'); setRecurringValue(interval / 1440)
    } else {
      setRecurringUnit('hours'); setRecurringValue(Math.max(12, Math.round(interval / 60)))
    }

    // Load stop condition
    const maxOcc = campaign.metadata?.max_occurrences
    const endDate = campaign.metadata?.recurring_end_date
    if (maxOcc != null) {
      setStopCondition('after_runs')
      setMaxOccurrences(Number(maxOcc))
    } else if (endDate) {
      setStopCondition('on_date')
      setRecurringEndDate(endDate.slice(0, 16)) // trim to datetime-local format
    } else {
      setStopCondition('never')
    }

    setTemplateId(campaign.template?.id ?? '')
    // setMetadataJson(JSON.stringify(campaign.metadata ?? {}, null, 2))
    // setTargetSegmentJson(JSON.stringify(campaign.targetSegment ?? {}, null, 2))
    // Recipients removed

    // In edit mode, we'd need to reconstruct enabledChannels from actual selected variants
    // For now defaulting to empty or inferred could be tricky without extra data.
    // If the campaign has enabledChannels, use them.
    if (campaign.enabledChannels) {
      const raw = campaign.enabledChannels as unknown
      let channels: CampaignChannel[] = []

      if (Array.isArray(raw)) {
        channels = raw as CampaignChannel[]
      } else if (typeof raw === 'string') {
        // Try parsing JSON first, then comma split
        try {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) channels = parsed
        } catch (e) {
          channels = (raw as string).split(',').map(c => c.trim()) as CampaignChannel[]
        }
      }
      setEnabledChannels(channels)
    }

    // Since we don't have strategy on ICampaign type in frontend yet, default or try to read
    // @ts-ignore
    if (campaign.strategy) {
      // @ts-ignore
      setStrategy(campaign.strategy)
    }
  }, [campaign])

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === templateId),
    [templates, templateId]
  )

  const availableVariants = selectedTemplate?.variants || []

  // Update selectedVariantIds when enabledChannels or selectedTemplate changes
  useEffect(() => {
    if (!selectedTemplate) {
      setSelectedVariantIds([])
      return
    }
    const newVariantIds: string[] = []

    enabledChannels.forEach(channel => {
      // Find default or first variant for this channel
      const variant = selectedTemplate.variants?.find(v => v.channel === channel && v.isDefault)
        || selectedTemplate.variants?.find(v => v.channel === channel)

      if (variant) {
        newVariantIds.push(variant.id)
      }
    })
    setSelectedVariantIds(newVariantIds)
  }, [enabledChannels, selectedTemplate])

  const toggleChannel = (channel: CampaignChannel) => {
    setEnabledChannels(prev => {
      if (prev.includes(channel)) {
        return prev.filter(c => c !== channel)
      }
      return [...prev, channel]
    })
  }

  const handleSubmit = async () => {
    // Validation: Check if all enabled channels are supported by template
    const unsupportedChannels = enabledChannels.filter(ch =>
      !availableVariants.some(v => v.channel === ch)
    )

    if (unsupportedChannels.length > 0) {
      alert(`The selected template does not support the following channels: ${unsupportedChannels.join(', ')}. Please add variants to the template or disable these channels.`)
      return
    }

    const parseSafe = (value: string) => {
      try {
        const parsed = JSON.parse(value || '{}')
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch (error) {
        return {}
      }
    }
    const payload: CampaignFormValues = {
      name,
      description,
      scheduleType,
      scheduledTime: scheduledTime ? new Date(scheduledTime).toISOString() : null,
      recurringRule: null,
      selectedVariantIds,
      templateId: templateId || null,
      metadata: {
        ...parseSafe(metadataJson),
        recurring_interval_minutes: recurringIntervalMinutes,
        ...(scheduleType === 'RECURRING' && stopCondition === 'after_runs' ? { max_occurrences: maxOccurrences } : { max_occurrences: null }),
        ...(scheduleType === 'RECURRING' && stopCondition === 'on_date' && recurringEndDate
          ? { recurring_end_date: new Date(recurringEndDate).toISOString() }
          : { recurring_end_date: null }),
      },
      targetSegment: parseSafe(targetSegmentJson),
      strategy,
      enabledChannels
    }
    await onSubmit(payload)
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>{campaign ? 'Edit campaign' : 'Create campaign'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='row g-4'>
            <div className='col-md-6'>
              <Form.Group className='mb-4'>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder='My new campaign'
                  autoFocus
                />
              </Form.Group>
              <Form.Group className='mb-4'>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder='Campaign goal or internal notes'
                />
              </Form.Group>
            </div>
            <div className='col-md-6'>
              <Form.Group className='mb-4'>
                <Form.Label>Template</Form.Label>
                <Form.Select
                  value={templateId}
                  onChange={(event) => setTemplateId(event.target.value)}
                >
                  <option value=''>Select a template</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Form.Select>
                {selectedTemplate && (
                  <div className='mt-2 text-muted small'>{selectedTemplate.description}</div>
                )}
              </Form.Group>

              {/* Channels Selector & Strategy */}
              <Form.Group className='mb-4'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <Form.Label className='mb-0'>Channels & Strategy</Form.Label>
                  <div className='form-check form-switch'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='strategySwitch'
                      checked={strategy === 'WATERFALL'}
                      onChange={(e) => setStrategy(e.target.checked ? 'WATERFALL' : 'BROADCAST')}
                    />
                    <label className='form-check-label small' htmlFor='strategySwitch'>
                      {strategy === 'WATERFALL' ? 'Smart Priority' : 'Broadcast to All'}
                    </label>
                  </div>
                </div>

                {strategy === 'WATERFALL' && (
                  <div className='alert alert-info py-2 px-3 small mb-2'>
                    <i className='bi bi-info-circle me-2'></i>
                    System will try channels in the order below. If a message is sent successfully, subsequent channels will be skipped.
                  </div>
                )}

                <div className='d-flex flex-column gap-2'>
                  {/* Active Channels List (Reorderable) */}
                  {enabledChannels.map((channel, index) => {
                    // Check if channel is supported by template
                    const isSupported = availableVariants.some(v => v.channel === channel)

                    return (
                      <div key={channel} className={`border rounded p-2 d-flex align-items-center justify-content-between ${!isSupported ? 'bg-light text-muted' : 'bg-white'}`}>
                        <div className='d-flex align-items-center'>
                          <div className='me-3 d-flex flex-column'>
                            <i className='bi bi-chevron-up role-button text-muted'
                              style={{ cursor: index > 0 ? 'pointer' : 'default', opacity: index > 0 ? 1 : 0.3, fontSize: '0.8rem' }}
                              onClick={() => {
                                if (index > 0) {
                                  const newChannels = [...enabledChannels]
                                  const temp = newChannels[index]
                                  newChannels[index] = newChannels[index - 1]
                                  newChannels[index - 1] = temp
                                  setEnabledChannels(newChannels)
                                }
                              }}
                            ></i>
                            <i className='bi bi-chevron-down role-button text-muted'
                              style={{ cursor: index < enabledChannels.length - 1 ? 'pointer' : 'default', opacity: index < enabledChannels.length - 1 ? 1 : 0.3, fontSize: '0.8rem' }}
                              onClick={() => {
                                if (index < enabledChannels.length - 1) {
                                  const newChannels = [...enabledChannels]
                                  const temp = newChannels[index]
                                  newChannels[index] = newChannels[index + 1]
                                  newChannels[index + 1] = temp
                                  setEnabledChannels(newChannels)
                                }
                              }}
                            ></i>
                          </div>
                          <ChannelBadge channel={channel} />
                          <span className='ms-2 fw-bold'>{channel}</span>
                          {!isSupported && <span className='badge bg-warning text-dark ms-2'>Not in template</span>}
                        </div>
                        <Button variant='link' className='text-danger p-0' onClick={() => toggleChannel(channel)}>
                          <i className='bi bi-x-lg'></i>
                        </Button>
                      </div>
                    )
                  })}

                  {/* Add Channel Dropdown/Buttons */}
                  <div className='d-flex gap-2 mt-2 flex-wrap'>
                    {channelOptions.filter(c => !enabledChannels.includes(c)).map(channel => {
                      const isSupported = availableVariants.some(v => v.channel === channel)
                      return (
                        <Button
                          key={channel}
                          variant='outline-secondary'
                          size='sm'
                          className={`d-flex align-items-center ${!templateId || !isSupported ? 'opacity-50' : ''}`}
                          disabled={!templateId}
                          onClick={() => toggleChannel(channel)}
                        >
                          <i className='bi bi-plus me-1'></i>
                          {channel}
                        </Button>
                      )
                    })}
                  </div>
                  {!templateId && <small className='text-muted'>Select a template to add channels</small>}
                </div>
              </Form.Group>
            </div>

            {/* Recipients Summary */}
            {/* Schedule Section */}
            <hr className="my-4" />
            <div className="col-12">
              <Form.Label className="fw-bold mb-3">Scheduling</Form.Label>
              <div className="d-flex gap-4 mb-4">
                <Form.Check
                  type="radio"
                  label="Immediate"
                  name="scheduleType"
                  id="schedule-immediate"
                  checked={scheduleType === 'IMMEDIATE'}
                  onChange={() => setScheduleType('IMMEDIATE')}
                />
                <Form.Check
                  type="radio"
                  label="Scheduled"
                  name="scheduleType"
                  id="schedule-scheduled"
                  checked={scheduleType === 'SCHEDULED'}
                  onChange={() => setScheduleType('SCHEDULED')}
                />
                <Form.Check
                  type="radio"
                  label="Recurring"
                  name="scheduleType"
                  id="schedule-recurring"
                  checked={scheduleType === 'RECURRING'}
                  onChange={() => setScheduleType('RECURRING')}
                />
              </div>

              {(scheduleType === 'SCHEDULED' || scheduleType === 'RECURRING') && (
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small">{scheduleType === 'RECURRING' ? 'Next execution' : 'Execution time'}</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  {scheduleType === 'RECURRING' && (
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small">Repeat every</Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Select
                            value={recurringUnit}
                            onChange={(e) => {
                              const unit = e.target.value as typeof recurringUnit
                              setRecurringUnit(unit)
                              // Reset to sensible default when switching unit
                              if (unit === 'hours') setRecurringValue(12)
                              else if (unit === 'days') setRecurringValue(1)
                              else if (unit === 'weeks') setRecurringValue(1)
                              else setRecurringValue(1)
                            }}
                            style={{ maxWidth: 120 }}
                          >
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                            <option value="weeks">Weeks</option>
                            <option value="months">Months</option>
                          </Form.Select>
                          <Form.Select
                            value={recurringValue}
                            onChange={(e) => setRecurringValue(Number(e.target.value))}
                          >
                            {recurringUnit === 'hours' &&
                              [12, 24, 48].map(h => (
                                <option key={h} value={h}>{h} hours</option>
                              ))}
                            {recurringUnit === 'days' &&
                              [1, 2, 3, 5, 7, 10, 14, 15, 30].map(d => (
                                <option key={d} value={d}>{d} {d === 1 ? 'day' : 'days'}</option>
                              ))}
                            {recurringUnit === 'weeks' &&
                              [1, 2, 3, 4].map(w => (
                                <option key={w} value={w}>{w} {w === 1 ? 'week' : 'weeks'}</option>
                              ))}
                            {recurringUnit === 'months' &&
                              [1, 2, 3, 6, 12].map(m => (
                                <option key={m} value={m}>{m === 12 ? '1 year' : `${m} ${m === 1 ? 'month' : 'months'}`}</option>
                              ))}
                          </Form.Select>
                        </div>
                        <Form.Text className="text-muted">
                          Min. 12 hours · ~{Math.round(recurringIntervalMinutes / 1440)} day(s) between runs
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              )}

              {/* Stop condition — only for RECURRING */}
              {scheduleType === 'RECURRING' && (
                <Row className="g-3 mt-1">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold">Stop condition</Form.Label>
                      <div className="d-flex gap-3 align-items-start flex-wrap mt-1">
                        <Form.Check
                          type="radio"
                          id="stop-never"
                          label="Never (manual pause)"
                          name="stopCondition"
                          checked={stopCondition === 'never'}
                          onChange={() => setStopCondition('never')}
                          className="mb-0"
                        />
                        <Form.Check
                          type="radio"
                          id="stop-after-runs"
                          label="After N runs"
                          name="stopCondition"
                          checked={stopCondition === 'after_runs'}
                          onChange={() => setStopCondition('after_runs')}
                          className="mb-0"
                        />
                        <Form.Check
                          type="radio"
                          id="stop-on-date"
                          label="On specific date"
                          name="stopCondition"
                          checked={stopCondition === 'on_date'}
                          onChange={() => setStopCondition('on_date')}
                          className="mb-0"
                        />
                      </div>

                      {stopCondition === 'after_runs' && (
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <span className="text-muted small">Stop after</span>
                          <Form.Select
                            value={maxOccurrences}
                            onChange={(e) => setMaxOccurrences(Number(e.target.value))}
                            style={{ maxWidth: 120 }}
                            size="sm"
                          >
                            {[2, 3, 4, 5, 6, 10, 12, 15, 20, 24, 30, 52].map(n => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </Form.Select>
                          <span className="text-muted small">executions</span>
                        </div>
                      )}

                      {stopCondition === 'on_date' && (
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <Form.Control
                            type="datetime-local"
                            value={recurringEndDate}
                            onChange={(e) => setRecurringEndDate(e.target.value)}
                            size="sm"
                            style={{ maxWidth: 260 }}
                          />
                          <span className="text-muted small">Campaign auto-completes on this date</span>
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </div>

            {/* Recipients Summary removed */}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='light' onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={
              loading ||
              !name ||
              !templateId ||
              enabledChannels.length === 0
            }
          >
            {campaign ? 'Save changes' : 'Create campaign'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
